/**
 * @file conversation-list-merge.ts
 * @description Чистые правила склейки списка диалогов при тихом обновлении
 * и «Показать ещё» (chat-window-design.md §2, §6).
 *
 * Сервер отдаёт не больше 100 строк за раз. Если человек долистал дальше,
 * тихое обновление перечитывает начало (сколько можно) и оставляет
 * догруженный хвост как есть — список не укорачивается.
 */

import type { StaffConversation } from "../../lib/api-chat";

/** Предел строк за запрос (сервер: LIST_LIMIT_MAX). */
export const LIST_LIMIT_MAX = 100;

/** Сколько строк перечитать при тихом обновлении. */
export function refreshLimit(loaded: number, page: number): number {
  return Math.min(LIST_LIMIT_MAX, Math.max(page, loaded));
}

export interface MergedList {
  items: StaffConversation[];
  cursor: string | null;
}

/**
 * Склейка обновлённого начала с загруженным списком.
 * Загружено не больше, чем перечитали, или список стал короче — берём
 * ответ целиком. Иначе — свежее начало + прежний хвост без повторов и
 * прежний курсор (он указывает на конец хвоста).
 */
export function mergeRefreshedHead(
  prev: StaffConversation[],
  prevCursor: string | null,
  head: { items: StaffConversation[]; nextCursor: string | null },
  limit: number,
): MergedList {
  if (prev.length <= limit || head.nextCursor === null) return { items: head.items, cursor: head.nextCursor };
  const seen = new Set(head.items.map((c) => c.id));
  const tail = prev.slice(limit).filter((c) => !seen.has(c.id));
  return { items: [...head.items, ...tail], cursor: prevCursor };
}

/** «Показать ещё»: дописать страницу без повторов. */
export function appendPage(prev: StaffConversation[], page: StaffConversation[]): StaffConversation[] {
  const seen = new Set(prev.map((c) => c.id));
  return [...prev, ...page.filter((c) => !seen.has(c.id))];
}
